// TV-Guide (EPG) Version 2 - reine Zeit- und Layoutfunktionen.
//
// Bewusst getrennt von helpers/EpgGuide.js (Version 1): die alte Version rechnet
// mit festen Seiten (8 Kanaele x 4h) und mutiert die Programmobjekte, um den
// Fokus zu markieren. Version 2 arbeitet stattdessen mit einem gleitenden
// Zeitfenster ueber bis zu 48 Stunden und haelt den Fokus ausserhalb der Daten.
// Alle Funktionen hier sind seiteneffektfrei und ohne Vue/Store-Bezug, damit sie
// einzeln testbar bleiben.
//
// Alle Zeiten sind Millisekunden (Date.getTime()), nicht Sekunden.

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;

// Gesamter abgedeckter Zeitraum ab Fensterbeginn.
export const TOTAL_HOURS = 48;

// Sichtbare Breite der Zeitachse. Zwei Stunden passen auf einen TV-Screen noch
// lesbar; alles darueber macht kurze Sendungen unklickbar schmal.
export const VIEW_MINUTES = 120;

// Raster der Zeitachsen-Beschriftung.
export const SLOT_MINUTES = 30;

// Kleinste dargestellte Sendungsbreite in Prozent des Sichtfensters. Ohne das
// verschwinden 5-Minuten-Sendungen (Nachrichten, Wetter) zu einem Strich und
// sind per Fernbedienung nicht mehr zu treffen.
export const MIN_PROGRAM_PERCENT = 3;

// Fensterbeginn auf das vorherige Raster abrunden (…:00 oder …:30).
export function alignToSlot(date, slotMinutes = SLOT_MINUTES) {
  const d = new Date(date instanceof Date ? date.getTime() : date);
  d.setSeconds(0, 0);
  d.setMinutes(Math.floor(d.getMinutes() / slotMinutes) * slotMinutes);
  return d;
}

// Zeitfenster, das die Achse aufspannt: von der abgerundeten Startzeit bis
// TOTAL_HOURS spaeter.
export function buildWindow(now, totalHours = TOTAL_HOURS) {
  const start = alignToSlot(now);
  return {
    start: start.getTime(),
    end: start.getTime() + totalHours * HOUR,
    totalHours: totalHours,
  };
}

// Beschriftung der Zeitachse fuer das gesamte Fenster. Pro Slot ein Eintrag mit
// Offset in Prozent, damit die Achse mit derselben Bezugsgroesse positioniert
// wird wie die Sendungen (siehe programLayout).
export function timeSlots(window, slotMinutes = SLOT_MINUTES) {
  const slots = [];
  const count = Math.floor(((window.end - window.start) / MINUTE) / slotMinutes);
  for (let i = 0; i < count; i++) {
    const at = window.start + i * slotMinutes * MINUTE;
    const d = new Date(at);
    slots.push({
      at: at,
      // Nur volle Stunden beschriften - halbe Stunden ergaenzen sonst nur
      // Rauschen auf der Achse.
      label: d.getMinutes() === 0 ? formatTime(d) : '',
      isDayStart: d.getHours() === 0 && d.getMinutes() === 0,
      dayLabel: formatDayShort(d),
      offsetPercent: percentOf(at - window.start, slotMinutes, count),
      widthPercent: 100 / count,
    });
  }
  return slots;
}

function percentOf(offsetMs, slotMinutes, count) {
  const totalMs = count * slotMinutes * MINUTE;
  if (totalMs <= 0) return 0;
  return (offsetMs / totalMs) * 100;
}

// Position und Breite einer Sendung, bezogen auf das GESAMTE Fenster (48h).
// Die Seite verschiebt dann nur den Container - dadurch muss beim Scrollen
// nichts neu gerechnet werden, was auf schwacher TV-Hardware den Unterschied
// zwischen ruckelnd und fluessig macht.
//
// Sendungen, die vor dem Fenster begonnen haben, werden am Fensterrand
// abgeschnitten; ihre angezeigte Startzeit bleibt die echte.
export function programLayout(program, window) {
  const total = window.end - window.start;
  if (total <= 0) return null;
  const startMs = program.startMs;
  const endMs = program.endMs;
  if (!(endMs > window.start) || !(startMs < window.end)) return null;

  const clampedStart = Math.max(startMs, window.start);
  const clampedEnd = Math.min(endMs, window.end);
  const left = ((clampedStart - window.start) / total) * 100;
  let width = ((clampedEnd - clampedStart) / total) * 100;

  // Mindestbreite bezogen auf das SICHTfenster, nicht auf die Gesamtbreite.
  const minWidth = (MIN_PROGRAM_PERCENT / 100) * (VIEW_MINUTES * MINUTE / total) * 100;
  if (width < minWidth) width = minWidth;

  return { leftPercent: left, widthPercent: width };
}

// Fortschritt einer laufenden Sendung in Prozent, sonst null.
// null statt 0 ist wichtig: 0 wuerde einen Balken bei einer gerade erst
// gestarteten Sendung nicht von "laeuft nicht" unterscheidbar machen.
export function programProgress(program, nowMs) {
  if (!program) return null;
  if (nowMs < program.startMs || nowMs >= program.endMs) return null;
  const duration = program.endMs - program.startMs;
  if (duration <= 0) return null;
  return ((nowMs - program.startMs) / duration) * 100;
}

export function isRunning(program, nowMs) {
  return !!program && nowMs >= program.startMs && nowMs < program.endMs;
}

// Das aktuell sichtbare Zeitfenster (VIEW_MINUTES breit) ab viewStartMs.
// Sendungen werden gegen DIESES Fenster positioniert, nicht gegen die 48h:
// so bleibt das DOM klein (nur die sichtbaren Sendungen) und beim Scrollen
// muessen nur die betroffenen Zeilen neu gerechnet werden.
export function viewWindow(viewStartMs, viewMinutes = VIEW_MINUTES) {
  return { start: viewStartMs, end: viewStartMs + viewMinutes * MINUTE };
}

// Haelt viewStartMs im erlaubten Bereich und auf dem Raster.
export function clampViewStart(viewStartMs, window, viewMinutes = VIEW_MINUTES) {
  const maxStart = window.end - viewMinutes * MINUTE;
  const clamped = Math.min(Math.max(viewStartMs, window.start), Math.max(window.start, maxStart));
  return alignToSlot(new Date(clamped)).getTime();
}

// Neuer Fensterbeginn, damit die uebergebene Sendung sichtbar wird.
//
// Die Sendung wird NICHT zentriert, sondern nur hereingeholt: sonst wandert die
// Zeitachse bei jedem Tastendruck und die Orientierung geht verloren.
export function viewStartFor(program, currentViewStartMs, window, viewMinutes = VIEW_MINUTES) {
  if (!program) return currentViewStartMs;
  const viewMs = viewMinutes * MINUTE;
  let start = currentViewStartMs;
  if (program.startMs < start) {
    start = program.startMs;
  } else if (program.endMs > start + viewMs) {
    // Ende einblenden, aber nie den Start aus dem Bild schieben.
    start = Math.min(program.startMs, program.endMs - viewMs);
  }
  return clampViewStart(start, window, viewMinutes);
}

// Position der "jetzt"-Markierung in Prozent des uebergebenen Fensters, oder
// null wenn die aktuelle Zeit ausserhalb liegt.
export function nowMarkerPercent(window, nowMs) {
  if (nowMs < window.start || nowMs > window.end) return null;
  return ((nowMs - window.start) / (window.end - window.start)) * 100;
}

// ---------------------------------------------------------------------------
// Normalisierung der API-Daten
// ---------------------------------------------------------------------------

// Wandelt die von helpers/EpgGuide.toJson gelieferten Events in das schlanke
// Format dieser Version. Bewusst nur Primitive plus zwei Zahlen fuer die Zeit:
// Date-Objekte pro Sendung im Vuex-State kosten auf 60+ Kanaelen deutlich mehr
// Speicher und machen jeden Vergleich teurer.
export function normalizePrograms(events, window) {
  if (!events || !events.length) return [];
  const out = [];
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const startMs = toMs(e.startISO);
    const endMs = toMs(e.endISO);
    if (startMs === null || endMs === null || endMs <= startMs) continue;
    // Ausserhalb des Fensters gar nicht erst uebernehmen - das haelt den State
    // klein, auch wenn die API mehr liefert als 48h.
    if (endMs <= window.start || startMs >= window.end) continue;
    out.push({
      id: e.id || String(startMs) + '-' + i,
      title: e.name || '',
      text: e.text || '',
      description: e.description || '',
      startMs: startMs,
      endMs: endMs,
      start: formatTime(new Date(startMs)),
      end: formatTime(new Date(endMs)),
    });
  }
  out.sort((a, b) => a.startMs - b.startMs);
  return out;
}

function toMs(value) {
  if (!value) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return isNaN(t) ? null : t;
  }
  const t = new Date(value).getTime();
  return isNaN(t) ? null : t;
}

// Kanaele aus der TV-Paket-Antwort: nur serviceType 'tv', nach position
// sortiert. Nimmt sowohl {channels:[...]} als auch ein rohes Array.
//
// Kanaele ohne epgChannelId werden behalten - sie erscheinen in der Liste, nur
// eben ohne Programme. Sie herauszufiltern wuerde die Kanalnummern gegenueber
// der TV-Kanalliste verschieben und den Gast verwirren.
export function sortTvChannels(source) {
  const list = (source && (source.channels || source)) || [];
  if (!list.length) return [];
  return list
    .filter((c) => c && c.serviceType === 'tv')
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0));
}

// Index der Sendung, die zu nowMs laeuft - sonst die naechste, sonst 0.
export function indexOfCurrentProgram(programs, nowMs) {
  if (!programs || !programs.length) return 0;
  for (let i = 0; i < programs.length; i++) {
    if (nowMs < programs[i].endMs) return i;
  }
  return programs.length - 1;
}

// ---------------------------------------------------------------------------
// Formatierung
// ---------------------------------------------------------------------------

export function formatTime(date) {
  return pad2(date.getHours()) + ':' + pad2(date.getMinutes());
}

export function formatDayShort(date) {
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return days[date.getDay()] + ' ' + pad2(date.getDate()) + '.' + pad2(date.getMonth() + 1) + '.';
}

function pad2(n) {
  return n < 10 ? '0' + n : String(n);
}

// Senderlogos. Basis-URL und Namensschema kommen aus der Build-Config
// (config/*.env.js bzw. .env), damit die Logos wahlweise vom Medienserver oder
// vom epg-logos-Dienst geladen werden koennen.
export const EPG_LOGO_BASE = String(
  process.env.EPG_LOGO_URL || 'https://media.digmedia.net/epgimg/'
).replace(/\/*$/, '/');

// 'source'  = <bild-id>.png, wobei die Bild-id das letzte Segment der
//             logo_url des Anbieters ist. Historisches Schema des
//             Medienservers.
// 'channel' = <channel_id>.png. Bleibt korrekt, wenn ein Sender ein neues
//             Logo bekommt: dann aendert sich die Bild-id, die channel_id
//             aber nicht.
export const EPG_LOGO_MODE = process.env.EPG_LOGO_MODE || 'source';

export const EPG_LOGO_FALLBACK = EPG_LOGO_BASE + '0.png';

// Bild-id aus der Anbieter-URL, z.B.
// https://cdn.images.simply.tv/1762429908621 -> 1762429908621
function logoIdFromUrl(logoUrl) {
  if (!logoUrl) return '';
  const withoutQuery = String(logoUrl).split('?')[0].split('#')[0];
  const parts = withoutQuery.split('/');
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (parts[i]) return parts[i];
  }
  return '';
}

export function epgLogoUrl(epgEntry) {
  if (!epgEntry) return EPG_LOGO_FALLBACK;

  if (EPG_LOGO_MODE === 'channel') {
    const id = epgEntry.channel_id;
    if (id === undefined || id === null || id === '') return EPG_LOGO_FALLBACK;
    return EPG_LOGO_BASE + id + '.png';
  }

  const last = logoIdFromUrl(epgEntry.logo_url);
  if (!last) return EPG_LOGO_FALLBACK;
  return EPG_LOGO_BASE + last + '.png';
}



// WEBPACK FOOTER //
// ./src/helpers/EpgGuide2.js